import { z } from "zod";
import type { Tool } from "@modelcontextprotocol/sdk/types.js";
import {
  CopilotKnowledgeFileError,
  createCopilotKnowledgeTextFileInputSchema,
  type CopilotKnowledgeFileApi,
} from "../api/copilot-knowledge-file-api.js";

export const createCopilotKnowledgeTextFileTool: Tool = {
  name: "create_copilot_knowledge_text_file",
  description:
    "EXPERIMENTAL storage-only creation of one uploaded Copilot text fixture in the current environment. Inline caller-supplied UTF-8 content only, at most 1 MiB, ASCII .txt/.md filename. Preview first; confirmation must repeat the exact inputs plus the returned plannedComponentId and expectedPlanSha256. Refuses caller-visible matching component names/schemas (not an atomic uniqueness guarantee). Creates a new type-14 row, uploads once and independently checks metadata and content hash. No existing-row replacement, retries, rollback, publication or ingestion; a partial failure returns the new/planned component ID for inspection, so do not retry blindly. Storage and readback do not prove knowledge readiness, search indexing or agent answers, and uploaded content can become accessible to agent users.",
  inputSchema: {
    ...z.toJSONSchema(createCopilotKnowledgeTextFileInputSchema, {
      target: "draft-7",
      io: "input",
    }),
    type: "object",
    allOf: [
      {
        if: { properties: { confirm: { const: true } }, required: ["confirm"] },
        then: { required: ["plannedComponentId", "expectedPlanSha256"] },
        else: {
          not: {
            anyOf: [{ required: ["plannedComponentId"] }, { required: ["expectedPlanSha256"] }],
          },
        },
      },
    ],
  } as Tool["inputSchema"],
  annotations: { readOnlyHint: false, destructiveHint: false, idempotentHint: false },
};

export async function handleCreateCopilotKnowledgeTextFile(
  context: { knowledgeFileApi: CopilotKnowledgeFileApi },
  args: unknown,
) {
  const input = createCopilotKnowledgeTextFileInputSchema.safeParse(args);
  if (!input.success) throw new CopilotKnowledgeFileError("input");
  const result = await context.knowledgeFileApi.createTextFile(input.data);
  return {
    text: result.notice,
    structured: { copilotKnowledgeFile: result },
    ...(result.mode === "confirmed-storage-attempt" && "failure" in result
      ? { isError: true }
      : {}),
  };
}
